import * as Sentry from '@sentry/react';
import { useState } from 'react';

import { useDynamicUpdateEventMutation } from '@mutations/EventMutations';
import { useUpdateScheduleDateDetailsMutation } from '@mutations/UserScheduleSubmissionsMutations';

import Button from '@components/buttons/Button';
import { ImageUploadField } from '@components/form/ImageUploadField';
import Card from '@components/layout/Card/Card';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

interface AddFlyerPopupContentProps {
  scheduleDateDetailsId?: string;
  eventId?: string;
  onClose: () => void;
}

export const AddFlyerPopupContent = ({
  scheduleDateDetailsId,
  eventId,
  onClose,
}: AddFlyerPopupContentProps) => {
  const [img, setImg] = useState<any>(undefined);
  const { updateScheduleDateDetailsAsync } = useUpdateScheduleDateDetailsMutation();
  const { updateEventAsync } = useDynamicUpdateEventMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = !!img && !isSubmitting;

  const handleSubmit = async () => {
    try {
      if (!canSubmit) return;

      setIsSubmitting(true);

      if (scheduleDateDetailsId) {
        await updateScheduleDateDetailsAsync({
          scheduleDateDetailsId,
          img,
        });
      } else if (eventId) {
        await updateEventAsync({
          eventId,
          eventImage: img,
        });
      }

      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <TitleToolbar text="Add flyer" size="md" />
      <div>
        <div
          className="py-2 px-3"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 8 }}
        >
          <ImageUploadField
            placeholder={<span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Select image</span>}
            imgStyle={{ width: '100%', height: 200, objectFit: 'contain', margin: '16px 0' }}
            onChange={setImg}
          />
        </div>
      </div>
      <div className="mt-2">
        <Button roundness="rounded" disabled={!canSubmit || isSubmitting} onClick={handleSubmit}>
          Upload
        </Button>
      </div>
    </div>
  );
};

interface AddFlyerPopupProps extends AddFlyerPopupContentProps {
  isVisible: boolean;
}

export const AddFlyerPopup = ({ isVisible, ...props }: AddFlyerPopupProps) => {
  return (
    <Popup isVisible={isVisible} onClose={props.onClose}>
      {isVisible && (
        <Popup.Content>
          <AddFlyerPopupContent {...props} />
        </Popup.Content>
      )}
    </Popup>
  );
};
